import { CLOUD_API_URL } from '../config/serverConfig';
import { tokenFpSync } from '../lib/authTokens';
import {
  getToken,
  getRefreshToken,
  readStoredAuthTokens,
  markRefreshFpSpent,
  isRefreshFpSpent,
  clearSpentRefreshFps,
} from './apiAuthStorage';
import { isConnectorAvailable, tryEnsureFreshToken, type EnsureFreshTokenResult } from '../lib/connector';
import { applyAuthDeclawShadow } from '../lib/authDeclawShadow';
import { isSessionDead, markSessionDead } from '../lib/sessionDead';
import { interceptInvoluntaryLogout } from '../lib/authDeclawShadow';
import {
  isBootTimeTerminalRejection,
  maybeLatchFromRefreshRejection,
  shouldSuppressAutomaticRefresh,
} from '../lib/authTerminalLatch';

function notifyBootTerminalSignInRequired(): void {
  if (!isBootTimeTerminalRejection()) return;
  void import('../components/AuthView').then((m) => {
    m.applyBootTerminalSignInRequired?.();
  });
}
import {
  classifyRefreshFailure,
  logRefreshFailed,
  logRefreshRequested,
  logSignoutDecision,
  logAlreadyUsedAbsorbed,
  logRefreshSingleFlightCoalesced,
  logRefreshDoubleAttemptBlocked,
  parseRefreshErrorDetails,
} from '../lib/authDiagnostics';
import type { ForcedRefreshLog } from './apiForcedRefreshLog';
import { logForcedRefreshFromSingleFlight } from './apiForcedRefreshLog';

/** Write the rotated refresh token to auth.json in the Tauri daemon context. */
async function syncRefreshTokenToDaemon(newRefreshToken: string, current: Record<string, unknown>): Promise<void> {
  if (typeof window === 'undefined' || !('__TAURI__' in window)) return;
  try {
    const { writeAuthJsonIfNeeded } = await import('../lib/authJsonWriteGate');
    const userId = (current?.user as any)?.id || '';
    const email = (current?.user as any)?.email || '';
    const fp = tokenFpSync(newRefreshToken);
    console.info(`[Auth:lifecycle] api.ts write-back auth.json fp=${fp}`);
    const result = await writeAuthJsonIfNeeded(newRefreshToken, userId, email, 'api.ts:sync');
    if (!result.written) {
      console.info(`[Auth:lifecycle] api.ts auth.json write-back not needed fp=${fp}`);
    }
  } catch (e) {
    console.warn('[Auth:lifecycle] api.ts write-back to auth.json SKIPPED:', e);
  }
}

/** Live access token from localStorage — always fresher than AuthView in-memory snapshot. */
export function getStoredAccessToken(): string | null {
  return getToken();
}

const API_FETCH_TIMEOUT_MS = 25_000;
/** Control Center connector list/publish can involve GitHub + R2 sync. */
export const CONNECTOR_ADMIN_TIMEOUT_MS = 90_000;

export type ApiRequestOpts = {
  headers?: Record<string, string>;
  noAuth?: boolean;
  /** Per-request timeout; defaults to API_FETCH_TIMEOUT_MS (25s). */
  timeoutMs?: number;
};

let _refreshPromise: Promise<string | null> | null = null;
/** Joiners waiting on `_refreshPromise` (excludes the leader). */
let _refreshWaiters = 0;
/** Fingerprint of the refresh token currently POSTed — blocks a second spend. */
let _refreshPostedFp: string | null = null;
let _refreshFailed = false;
let _lastRefreshFailure: { status: number; data: unknown } | null = null;
let _ensureFreshInvokeFailures = 0;

// Server explicitly rejected the refresh token (not a network error or already_used race)
let _refreshRejectedByServer = false;

const MAX_REFRESH_ATTEMPTS = 2;
const MAX_ENSURE_FRESH_INVOKE_FAILURES = 2;
/** One IPC retry after a timeout — the Connector's coalesce cache returns its finished refresh. */
const ENSURE_FRESH_IPC_RETRY_DELAY_MS = 500;
/** Re-read window after "Already Used" before any sign-out (poll storage + auth.json). */
const ALREADY_USED_ADOPT_ATTEMPTS = 10;

export function resetRefreshState() {
  _refreshFailed = false;
  _refreshRejectedByServer = false;
  _lastRefreshFailure = null;
  _ensureFreshInvokeFailures = 0;
  _refreshWaiters = 0;
  _refreshPostedFp = null;
  _refreshPromise = null;
  clearSpentRefreshFps();
}

export function isRefreshInFlight(): boolean {
  return _refreshPromise != null;
}

export function isRefreshRejectedByServer(): boolean {
  return _refreshRejectedByServer || shouldSuppressAutomaticRefresh();
}

/** Clear retry counters only — never un-latch fatal refresh rejection. */
function clearTransientRefreshRetries() {
  _ensureFreshInvokeFailures = 0;
}

function sessionDeadResponse(): Response {
  return new Response(
    JSON.stringify({ error: 'session_expired', session_dead: true }),
    { status: 401, headers: { 'Content-Type': 'application/json' } },
  );
}

function isEnsureFreshInvokeFailure(message: string): boolean {
  const lower = message.toLowerCase();
  return lower.includes('timeout')
    || lower.includes('failed after retries')
    || lower.includes('ipc transport probe timeout');
}

function refreshErrorMessage(data: unknown): string {
  if (!data || typeof data !== 'object') return '';
  const d = data as Record<string, unknown>;
  return String(d.error_description || d.error || d.msg || '');
}

/** Supabase single-use rotation race — daemon or another client refreshed first. */
export function isRefreshTokenAlreadyUsedError(data: unknown): boolean {
  const msg = refreshErrorMessage(data).toLowerCase();
  return msg.includes('already_used')
    || msg.includes('refresh_token_already_used')
    || msg.includes('already used');
}

function isRefreshTransient(status: number, data: unknown): boolean {
  if (status === 503) return true;
  if (data && typeof data === 'object' && (data as Record<string, unknown>).transient) return true;
  return false;
}

/**
 * After already_used, another holder (Connector, another tab) may still be writing the
 * rotated session — poll localStorage + auth.json. Returns the adopted access token, or
 * null when the stored session still carries the spent refresh token.
 */
async function adoptRefreshedCredentialsFromStorage(
  oldFp: string,
  attempts = ALREADY_USED_ADOPT_ATTEMPTS,
): Promise<string | null> {
  for (let i = 0; i < attempts; i++) {
    if (i > 0) {
      await new Promise((r) => setTimeout(r, 40 * i));
    }
    await syncRefreshTokenFromAuthJson();
    const after = readStoredAuthTokens();
    const afterFp = tokenFpSync(after.refreshToken || '');
    if (after.accessToken && after.refreshToken && afterFp !== oldFp && !isRefreshFpSpent(afterFp)) {
      try { logAlreadyUsedAbsorbed(afterFp); } catch { /* diagnostic only */ }
      return after.accessToken;
    }
  }
  return null;
}

/** Re-read auth.json after the daemon rotates the refresh token (Tauri/daemon context). */
async function syncRefreshTokenFromAuthJson(): Promise<boolean> {
  if (typeof window === 'undefined' || !('__TAURI__' in window)) return false;
  try {
    const { readAuthJson } = await import('../lib/connector');
    const creds = await readAuthJson();
    if (!creds?.refresh_token || creds.refresh_token.length < 4) return false;
    const stored = localStorage.getItem('maestro_auth');
    const current: Record<string, unknown> = stored ? JSON.parse(stored) : {};
    const oldFp = tokenFpSync(String(current.refreshToken || ''));
    const newFp = tokenFpSync(creds.refresh_token);
    if (oldFp === newFp) return false;
    console.info(
      `[Auth:lifecycle] api.ts synced refresh from auth.json old_fp=${oldFp} new_fp=${newFp}`,
    );
    localStorage.setItem('maestro_auth', JSON.stringify({
      ...current,
      refreshToken: creds.refresh_token,
      user: current.user || { id: creds.user_id, email: creds.email },
    }));
    return true;
  } catch (e) {
    console.warn('[Auth:lifecycle] api.ts auth.json sync skipped:', e);
    return false;
  }
}

function persistRefreshedSession(
  newToken: string,
  newRefreshToken: string,
  refreshToken: string,
  oldFp: string,
): string {
  const newFp = tokenFpSync(newRefreshToken);
  const stored = localStorage.getItem('maestro_auth');
  const current: Record<string, unknown> = stored ? JSON.parse(stored) : {};
  console.info(
    `[Auth:lifecycle] api.ts refresh success old_fp=${oldFp} new_fp=${newFp} ` +
    `rotated=${newRefreshToken !== refreshToken} localStorage=write auth.json=pending`,
  );
  localStorage.setItem('maestro_auth', JSON.stringify({
    ...current,
    accessToken: newToken,
    refreshToken: newRefreshToken,
  }));
  syncRefreshTokenToDaemon(newRefreshToken, current).catch(() => {});
  window.dispatchEvent(new CustomEvent('maestro:token-refreshed', {
    detail: { accessToken: newToken, refreshToken: newRefreshToken },
  }));
  _refreshFailed = false;
  _refreshRejectedByServer = false;
  return newToken;
}

function getAccessTokenExpMs(token: string | null): number | null {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    const expSec = Number(payload?.exp);
    return Number.isFinite(expSec) && expSec > 0 ? expSec * 1000 : null;
  } catch {
    return null;
  }
}

function isAccessTokenStale(bufferMs = 120_000): boolean {
  const token = getToken();
  const expMs = getAccessTokenExpMs(token);
  if (!expMs) return false;
  return Date.now() >= expMs - bufferMs;
}

/** Refresh the access token when expired or within 2 minutes of expiry. */
export async function ensureFreshAccessToken(): Promise<string | null> {
  const token = getToken();
  if (!token) return null;
  if (isSessionDead()) return null;
  if (!isAccessTokenStale()) return token;
  if (!getRefreshToken()) return token;
  if (_refreshRejectedByServer || shouldSuppressAutomaticRefresh()) return null;
  // Shares single-flight with 401 interceptor — never reset state here (avoids race with in-flight refresh).
  return tryRefreshToken('api.ts:ensureFreshAccessToken');
}

export type SharedRefreshResult = {
  accessToken: string | null;
  refreshToken: string | null;
  /** true when tryRefreshToken returned a new access token from the network */
  refreshed: boolean;
  /** Server explicitly rejected the refresh token (localStorage cleared) */
  fatalRejected: boolean;
  /** Lost single-use race but adopted fresher credentials from storage */
  alreadyUsedAbsorbed: boolean;
};

/**
 * Single-flight refresh for AuthView / AuthProvider — shares _refreshPromise with the
 * api.ts 401 interceptor so cold-boot races consume the token only once.
 */
export async function refreshAccessTokenShared(caller: string): Promise<SharedRefreshResult> {
  const before = readStoredAuthTokens();
  const beforeFp = tokenFpSync(before.refreshToken || '');

  if (!before.refreshToken) {
    return {
      accessToken: null,
      refreshToken: null,
      refreshed: false,
      fatalRejected: false,
      alreadyUsedAbsorbed: false,
    };
  }

  const newAccessToken = await tryRefreshToken(caller);
  const after = readStoredAuthTokens();
  const afterFp = tokenFpSync(after.refreshToken || '');

  if (_refreshRejectedByServer) {
    return {
      accessToken: null,
      refreshToken: null,
      refreshed: false,
      fatalRejected: true,
      alreadyUsedAbsorbed: false,
    };
  }

  if (newAccessToken) {
    return {
      accessToken: newAccessToken,
      refreshToken: after.refreshToken,
      refreshed: true,
      fatalRejected: false,
      alreadyUsedAbsorbed: false,
    };
  }

  const absorbed = !!(
    after.accessToken
    && after.refreshToken
    && (afterFp !== beforeFp || after.accessToken !== before.accessToken)
  );

  if (absorbed) {
    try { logAlreadyUsedAbsorbed(afterFp); } catch { /* diagnostic only */ }
    return {
      accessToken: after.accessToken,
      refreshToken: after.refreshToken,
      refreshed: false,
      fatalRejected: false,
      alreadyUsedAbsorbed: true,
    };
  }

  if (after.accessToken && after.refreshToken) {
    return {
      accessToken: after.accessToken,
      refreshToken: after.refreshToken,
      refreshed: false,
      fatalRejected: false,
      alreadyUsedAbsorbed: false,
    };
  }

  return {
    accessToken: null,
    refreshToken: null,
    refreshed: false,
    fatalRejected: false,
    alreadyUsedAbsorbed: false,
  };
}

/**
 * Force HTTP session validation after repeated BATON WS failures (e.g. bare 1006 + burned token).
 * Returns true only when the session was marked dead and re-auth should surface.
 *
 * Fatal vs transient is delegated to refreshAccessTokenShared → tryRefreshDirect:
 * - 401/403 invalid/burned token → rejectRefreshSession → markSessionDead (returns true)
 * - fetch error, timeout, 5xx/503 → credentials preserved, no markSessionDead (returns false)
 */
export async function probeSessionAuthAfterBatonFailures(caller: string): Promise<boolean> {
  if (isSessionDead()) return true;
  if (_refreshRejectedByServer) return true;
  if (!getRefreshToken()) {
    markSessionDead(`${caller}:no_refresh_token`);
    return true;
  }
  const result = await refreshAccessTokenShared(`${caller}:baton-ws-probe`);
  if (result.fatalRejected || isSessionDead()) return true;
  // Network/offline or transient HTTP failure — keep BATON reconnecting.
  return false;
}

function adoptConnectorRefreshResult(result: EnsureFreshTokenResult, oldFp: string): string {
  const newRefreshToken = result.refresh_token || getRefreshToken() || '';
  const newFp = tokenFpSync(newRefreshToken);
  const stored = localStorage.getItem('maestro_auth');
  const current: Record<string, unknown> = stored ? JSON.parse(stored) : {};
  console.info(
    `[Auth:lifecycle] api.ts ensure_fresh_token ok old_fp=${oldFp} new_fp=${newFp} ` +
    `rotated=${result.rotated} localStorage=write auth.json=connector-owned`,
  );
  localStorage.setItem('maestro_auth', JSON.stringify({
    ...current,
    accessToken: result.access_token,
    refreshToken: newRefreshToken,
  }));
  window.dispatchEvent(new CustomEvent('maestro:token-refreshed', {
    detail: { accessToken: result.access_token, refreshToken: newRefreshToken },
  }));
  _refreshFailed = false;
  _refreshRejectedByServer = false;
  return result.access_token;
}

function rejectRefreshSession(data: unknown, status: number, caller: string): null {
  maybeLatchFromRefreshRejection(status, data, refreshErrorMessage(data));
  if (interceptInvoluntaryLogout('api_refresh_rejected', `${caller}:refresh_rejected`, { status, data })) {
    return null;
  }
  notifyBootTerminalSignInRequired();
  _refreshFailed = true;
  _refreshRejectedByServer = true;
  _lastRefreshFailure = { status, data };
  try {
    const { errorCode } = parseRefreshErrorDetails(data);
    logSignoutDecision(errorCode, classifyRefreshFailure(status, data), status);
  } catch { /* diagnostic only */ }

  const declaw = applyAuthDeclawShadow(data, caller);
  if (declaw.suppression_applied) {
    return null;
  }

  localStorage.removeItem('maestro_auth');
  try { sessionStorage.setItem('maestro_refresh_failed', '1'); } catch { /* ignore */ }
  markSessionDead(`${caller}:refresh_rejected`);
  return null;
}

async function tryRefreshViaConnector(caller: string): Promise<string | null | 'legacy'> {
  try { logRefreshRequested(`${caller}:connector`, false, false); } catch { /* diagnostic only */ }
  const outcome = await tryEnsureFreshToken();
  if (outcome.kind === 'unsupported') return 'legacy';
  if (outcome.kind === 'error') {
    const msg = outcome.message;
    const msgLower = msg.toLowerCase();
    if (isEnsureFreshInvokeFailure(msg)) {
      _ensureFreshInvokeFailures += 1;
      console.warn(
        `[Auth:lifecycle] ensure_fresh_token invoke failure ${_ensureFreshInvokeFailures}/${MAX_ENSURE_FRESH_INVOKE_FAILURES}:`,
        msg,
      );
      // Connector owns rotation and keeps refreshing after our invoke times out. Never race
      // it with a web HTTP refresh (same single-use token → "Already Used"); retry the IPC.
      const before = readStoredAuthTokens();
      await new Promise((r) => setTimeout(r, ENSURE_FRESH_IPC_RETRY_DELAY_MS));
      const retry = await tryEnsureFreshToken();
      if (retry.kind === 'ok') {
        _ensureFreshInvokeFailures = 0;
        console.info('[Auth:lifecycle] ensure_fresh_token IPC retry ok — adopted Connector refresh');
        const oldFp = tokenFpSync(getRefreshToken() || retry.result.refresh_token || '');
        return adoptConnectorRefreshResult(retry.result, oldFp);
      }
      const after = readStoredAuthTokens();
      if (after.accessToken && after.accessToken !== before.accessToken) {
        _ensureFreshInvokeFailures = 0;
        return after.accessToken;
      }
      if (_ensureFreshInvokeFailures >= MAX_ENSURE_FRESH_INVOKE_FAILURES) _ensureFreshInvokeFailures = 0;
      console.warn(
        '[Auth:lifecycle] ensure_fresh_token IPC timeout — Connector owns refresh, no web HTTP refresh; credentials kept',
      );
      return null;
    }
    _ensureFreshInvokeFailures = 0;
    if (msgLower.includes('session_expired') || msgLower.includes('auth_family_revoked')) {
      maybeLatchFromRefreshRejection(401, { error: outcome.message, error_description: outcome.message });
      return rejectRefreshSession({ error: outcome.message }, 401, caller);
    }
    console.warn('[Auth:lifecycle] api.ts ensure_fresh_token failed — credentials preserved:', outcome.message);
    return null;
  }

  _ensureFreshInvokeFailures = 0;
  const refreshToken = getRefreshToken() || outcome.result.refresh_token;
  const oldFp = tokenFpSync(refreshToken || '');
  return adoptConnectorRefreshResult(outcome.result, oldFp);
}

async function tryRefreshDirect(caller = 'api.ts:tryRefreshDirect'): Promise<string | null> {
  for (let attempt = 0; attempt < MAX_REFRESH_ATTEMPTS; attempt++) {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return null;
    const oldFp = tokenFpSync(refreshToken);
    if (isRefreshFpSpent(oldFp)) {
      // Spent token still in storage — never replay it; adopt or give up cleanly.
      try { logRefreshDoubleAttemptBlocked('already_used_no_replay', oldFp); } catch { /* diagnostic only */ }
      const adopted = await adoptRefreshedCredentialsFromStorage(oldFp, 1);
      if (adopted) return adopted;
      if (isConnectorAvailable()) return null;
      return rejectRefreshSession({ error: 'Invalid Refresh Token: Already Used' }, 401, caller);
    }
    if (_refreshPostedFp && _refreshPostedFp === oldFp) {
      try { logRefreshDoubleAttemptBlocked('same_fp_inflight', oldFp); } catch { /* diagnostic only */ }
      return getToken();
    }
    _refreshPostedFp = oldFp;
    console.info(`[Auth:lifecycle] api.ts refresh attempt ${attempt + 1}/${MAX_REFRESH_ATTEMPTS} old_fp=${oldFp}`);
    const controller = new AbortController();
    const refreshTimer = setTimeout(() => controller.abort(), 10_000);
    let res: Response;
    try {
      res = await fetch(`${CLOUD_API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({ refresh_token: refreshToken }),
        signal: controller.signal,
      });
    } catch {
      console.warn('[Auth:lifecycle] api.ts refresh network error — credentials preserved');
      return null;
    } finally {
      clearTimeout(refreshTimer);
    }

    const data = await res.json().catch(() => null);
    const newToken = data?.session?.access_token || data?.access_token;
    if (res.ok && newToken) {
      const newRefreshToken = data?.session?.refresh_token || data?.refresh_token || refreshToken;
      return persistRefreshedSession(newToken, newRefreshToken, refreshToken, oldFp);
    }

    console.warn(
      `[Auth:lifecycle] api.ts refresh FAILED old_fp=${oldFp} status=${res.status} ` +
      `msg=${refreshErrorMessage(data).slice(0, 80)}`,
    );
    try { logRefreshFailed(res.status, data, caller); } catch { /* diagnostic only */ }

    if (isRefreshTransient(res.status, data)) {
      console.warn('[Auth:lifecycle] api.ts refresh transient failure — credentials preserved');
      return null;
    }

    const authFail = res.status === 401 || res.status === 403;
    if (authFail && (isRefreshTokenAlreadyUsedError(data) || classifyRefreshFailure(res.status, data) === 'already_used')) {
      // Another holder rotated first: re-read the stored session, never replay the spent token (#602).
      markRefreshFpSpent(oldFp);
      console.info('[Auth:lifecycle] api.ts refresh already_used — re-reading stored session');
      const adopted = await adoptRefreshedCredentialsFromStorage(oldFp);
      if (adopted) return adopted;
      try { logRefreshDoubleAttemptBlocked('already_used_no_replay', oldFp); } catch { /* diagnostic only */ }
      if (isConnectorAvailable()) return null;
      return rejectRefreshSession(data, res.status, caller);
    }

    if (res.status === 401 || res.status === 403) {
      const synced = await syncRefreshTokenFromAuthJson();
      if (synced && attempt + 1 < MAX_REFRESH_ATTEMPTS) continue;
      return rejectRefreshSession(data, res.status, caller);
    }

    console.warn('[Auth:lifecycle] api.ts refresh transient HTTP failure — credentials preserved');
    return null;
  }
  return getToken();
}

async function tryRefreshToken(
  caller = 'api.ts:tryRefreshToken',
  forcedLog?: ForcedRefreshLog,
): Promise<string | null> {
  if (_refreshFailed || isSessionDead() || shouldSuppressAutomaticRefresh()) return null;
  if (_refreshPromise) {
    if (forcedLog) logForcedRefreshFromSingleFlight(forcedLog, true);
    _refreshWaiters += 1;
    const waiters = _refreshWaiters;
    try { logRefreshRequested(caller, true, true); } catch { /* diagnostic only */ }
    try { logRefreshSingleFlightCoalesced(waiters, caller); } catch { /* diagnostic only */ }
    try {
      return await _refreshPromise;
    } finally {
      _refreshWaiters = Math.max(0, _refreshWaiters - 1);
    }
  }
  if (forcedLog) logForcedRefreshFromSingleFlight(forcedLog, false);
  try { logRefreshRequested(caller, false, false); } catch { /* diagnostic only */ }
  _refreshPromise = (async () => {
    try {
      if (isConnectorAvailable()) {
        const viaConnector = await tryRefreshViaConnector(caller);
        if (viaConnector !== 'legacy') return viaConnector;
      }
      return await tryRefreshDirect(caller);
    } finally {
      _refreshPostedFp = null;
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
}

function forceSignOut(): void {
  if (interceptInvoluntaryLogout('api_force_signout', 'forceSignOut')) {
    return;
  }
  const hadSession = !!localStorage.getItem('maestro_auth');
  if (_refreshRejectedByServer) {
    try {
      const failure = _lastRefreshFailure;
      const { errorCode } = parseRefreshErrorDetails(failure?.data);
      const classified = failure
        ? classifyRefreshFailure(failure.status, failure.data)
        : 'fatal';
      logSignoutDecision(errorCode, classified, failure?.status);
    } catch { /* diagnostic only */ }
    localStorage.removeItem('maestro_auth');
    try { sessionStorage.setItem('maestro_refresh_failed', '1'); } catch {}
  }
  if (hadSession && _refreshRejectedByServer) {
    markSessionDead('forceSignOut');
  }
  throw new Error('Session expired');
}

async function fetchWithAuth(
  url: string,
  init: RequestInit,
  noAuth?: boolean,
  timeoutMs: number = API_FETCH_TIMEOUT_MS,
): Promise<Response> {
  if (isSessionDead() || shouldSuppressAutomaticRefresh() || isBootTimeTerminalRejection()) {
    return sessionDeadResponse();
  }

  const headers = { ...(init.headers as Record<string, string> | undefined) };
  if (!noAuth) {
    // Await the single-flight refresh — never race a 10s timeout then send a
    // stale Bearer while /api/auth/refresh is still in flight (#602).
    const fresh = await ensureFreshAccessToken();
    const token = fresh || getStoredAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const opts = { ...init, headers, credentials: 'omit' as RequestCredentials, signal: controller.signal };
  let res: Response;
  try {
    res = await fetch(url, opts);
  } catch (e) {
    clearTimeout(timer);
    if (e instanceof Error && e.name === 'AbortError') {
      throw new Error(`Request timed out after ${timeoutMs / 1000}s`);
    }
    throw e;
  }
  clearTimeout(timer);
  if (res.status !== 401 || noAuth) return res;

  // FormData / stream bodies are single-use — callers must rebuild for retry (see uploadMultipart).
  if (init.body instanceof FormData) return res;

  if (isSessionDead() || _refreshRejectedByServer || shouldSuppressAutomaticRefresh()) {
    if (!shouldSuppressAutomaticRefresh()) {
      return res;
    }
    return sessionDeadResponse();
  }

  if (!getToken()) return res;

  // Single-flight: concurrent 401s share one refresh; never clear fatal rejection latch.
  if (!_refreshPromise) clearTransientRefreshRetries();
  const newToken = await tryRefreshToken('api.ts:401-interceptor', { kind: '401', url });
  const retryToken = getStoredAccessToken() || newToken;
  if (!retryToken) {
    if (_refreshRejectedByServer) forceSignOut();
    return res;
  }
  const retryHeaders = { ...(init.headers as Record<string, string>), Authorization: `Bearer ${retryToken}` };
  const retryController = new AbortController();
  const retryTimer = setTimeout(() => retryController.abort(), timeoutMs);
  try {
    return await fetch(url, { ...opts, headers: retryHeaders, signal: retryController.signal });
  } finally {
    clearTimeout(retryTimer);
  }
}

async function handleResponse(res: Response) {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${body}`);
  }
  return res.json();
}

function getMaestroLocale(): string {
  try {
    return localStorage.getItem('maestro_language') || 'en';
  } catch {
    return 'en';
  }
}

function authHeaders(extra?: Record<string, string>, noAuth?: boolean): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  if (!noAuth && !isSessionDead()) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  headers['X-Maestro-Locale'] = getMaestroLocale();
  return headers;
}

export const api = {
  get: async (path: string, opts?: ApiRequestOpts) => {
    const headers = authHeaders(opts?.headers, opts?.noAuth);
    const res = await fetchWithAuth(`${CLOUD_API_URL}${path}`, { headers }, opts?.noAuth, opts?.timeoutMs);
    return handleResponse(res);
  },

  post: async (path: string, body?: unknown, opts?: ApiRequestOpts) => {
    const headers = authHeaders({ 'Content-Type': 'application/json', ...opts?.headers }, opts?.noAuth);
    const res = await fetchWithAuth(`${CLOUD_API_URL}${path}`, {
      method: 'POST',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    }, opts?.noAuth, opts?.timeoutMs);
    return handleResponse(res);
  },

  put: async (path: string, body?: unknown, opts?: ApiRequestOpts) => {
    const headers = authHeaders({ 'Content-Type': 'application/json', ...opts?.headers });
    const res = await fetchWithAuth(`${CLOUD_API_URL}${path}`, {
      method: 'PUT',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    }, undefined, opts?.timeoutMs);
    return handleResponse(res);
  },

  patch: async (path: string, body?: unknown, opts?: ApiRequestOpts) => {
    const headers = authHeaders({ 'Content-Type': 'application/json', ...opts?.headers });
    const res = await fetchWithAuth(`${CLOUD_API_URL}${path}`, {
      method: 'PATCH',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    }, undefined, opts?.timeoutMs);
    return handleResponse(res);
  },

  delete: async (path: string, opts?: ApiRequestOpts) => {
    const headers = authHeaders(opts?.headers);
    const res = await fetchWithAuth(`${CLOUD_API_URL}${path}`, { method: 'DELETE', headers }, undefined, opts?.timeoutMs);
    return handleResponse(res);
  },

  raw: async (path: string, init?: RequestInit) => {
    if (isSessionDead()) {
      return sessionDeadResponse();
    }
    const token = getToken();
    const headers: Record<string, string> = { ...((init?.headers || {}) as Record<string, string>) };
    if (token && !headers.Authorization) headers.Authorization = `Bearer ${token}`;
    return fetchWithAuth(`${CLOUD_API_URL}${path}`, { ...(init || {}), headers });
  },
};

/**
 * Multipart upload with a fresh FormData body on 401 retry (streams are single-use).
 */
export async function uploadMultipart(
  path: string,
  fieldName: string,
  file: File,
  opts?: { signal?: AbortSignal; timeoutMs?: number },
): Promise<Response> {
  if (isSessionDead()) {
    return sessionDeadResponse();
  }

  const timeoutMs = opts?.timeoutMs ?? 60_000;
  const parentSignal = opts?.signal;

  const uploadOnce = (token: string | null) => {
    const form = new FormData();
    form.append(fieldName, file, file.name);
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const onParentAbort = () => controller.abort();
    parentSignal?.addEventListener('abort', onParentAbort, { once: true });
    return fetch(`${CLOUD_API_URL}${path}`, {
      method: 'POST',
      headers,
      body: form,
      credentials: 'omit',
      signal: controller.signal,
    }).finally(() => {
      clearTimeout(timer);
      parentSignal?.removeEventListener('abort', onParentAbort);
    });
  };

  let res = await uploadOnce((await ensureFreshAccessToken()) || getStoredAccessToken());
  if (res.status === 401 && getToken() && !isSessionDead() && !_refreshRejectedByServer) {
    if (!_refreshPromise) clearTransientRefreshRetries();
    const newToken = await tryRefreshToken('api.ts:uploadMultipart', { kind: 'upload_401', url: path });
    const retryToken = getStoredAccessToken() || newToken;
    if (!retryToken) {
      if (_refreshRejectedByServer) forceSignOut();
      return res;
    }
    res = await uploadOnce(retryToken);
  }
  return res;
}

/**
 * Raw fetch to Mycelium with the same 401 → refresh → retry-once behavior as api.get/post.
 * Use for endpoints not covered by api.* (e.g. tunnel poll, batched events).
 */
export async function fetchCloudAuthenticated(
  path: string,
  init?: RequestInit,
  opts?: ApiRequestOpts,
): Promise<Response> {
  if (!opts?.noAuth && isSessionDead()) {
    return sessionDeadResponse();
  }
  const headers = authHeaders(init?.headers as Record<string, string> | undefined, opts?.noAuth);
  return fetchWithAuth(`${CLOUD_API_URL}${path}`, { ...init, headers }, opts?.noAuth, opts?.timeoutMs);
}

export default api;
